<script lang="ts">
    import { fade, slide } from 'svelte/transition';
    import { useComparisonContext } from '@shared/providers/comparison-context/ComparisonContext.browser';
    import { injectWiring } from '@tilelist/application/wiring/TileListWiringContext.svelte.ts';
    import ComparisonTile from './ComparisonTile.svelte';

    interface Props {
        rule: string;
    }

    let { rule }: Props = $props();

    const context = useComparisonContext();
    const wiring = injectWiring(['trackingService']);

    function toggleOpen() {
        open = !open;
        wiring.trackingService.trackComparisonActivity(open ? 'sheet_open' : 'sheet_close');
    }

    let numberOfSelectedVariations = context.selectedVariations.length;

    $effect(() => {
        if (context.initialized && context.selectedVariations.length > numberOfSelectedVariations) {
            open = true;
        }
        numberOfSelectedVariations = context.selectedVariations.length;
    });

    let open = $state(false);

    const NO_SCROLL_CLASS = 'reptile-comparison--no-scroll';
    $effect(() => {
        if (open && renderComparisonSheet) {
            document.documentElement.classList.add(NO_SCROLL_CLASS);
        }
        return () => {
            document.documentElement.classList.remove(NO_SCROLL_CLASS);
        };
    });

    const selectedVariationCount = $derived(context.selectedVariations.length);
    const productCountLabel = $derived(selectedVariationCount === 1 ? 'Produkt' : 'Produkte');

    async function onDeleteAllClicked() {
        wiring.trackingService.trackComparisonActivity('deselected_all');
        await context.clearSelectedVariations();
    }

    const renderComparisonSheet = $derived(
        context.showComparison && context.comparisonId && selectedVariationCount > 0,
    );

    let compareEventSent = false;
    $effect(() => {
        /*                                                                    */
        /*                               */
        if (renderComparisonSheet) {
            wiring.eventsOutgoing?.notifyComparisonOpened();
            compareEventSent = true;
        } else if (compareEventSent) {
            wiring.eventsOutgoing?.notifyComparisonClosed();
        }
    });

    function onCompareLinkClicked() {
        wiring.trackingService.trackComparisonActivity('start');
    }
</script>

{#if renderComparisonSheet}
    <div class="reptile-comparison-sheet" data-qa="reptile-product-comparison-sheet">
        <!-- svelte-ignore a11y_click_events_have_key_events -->
        <!-- svelte-ignore a11y_no_static_element_interactions -->
        <div class="reptile-comparison-sheet__header">
            <oc-row-v2
                class="reptile-comparison-sheet__header__row"
                icon-right={open ? 'arrow-down' : 'arrow-up'}
                hide-divider
                as-button
                onclick={toggleOpen}
            >
                <span class="oc-headline-50">
                    Produktvergleich ({selectedVariationCount}
                    {productCountLabel})
                </span>
            </oc-row-v2>
        </div>
        {#if open}
            <div out:slide>
                <div class="reptile-comparison-sheet__tiles">
                    <oc-cinema-v1 resizing="none">
                        {#each context.selectedVariations as variation (variation.variationId)}
                            <ComparisonTile {variation} {rule} />
                        {/each}
                        {#if selectedVariationCount === 1}
                            <oc-banner-v1
                                variant="hint"
                                hide-icon
                                hide-close-button
                                class="reptile-comparison-sheet__hint"
                                >Füge doch noch ein zweites Produkt von dieser Liste hinzu.</oc-banner-v1
                            >
                        {/if}
                    </oc-cinema-v1>
                </div>
                <div class="reptile-comparison-sheet__footer">
                    <!-- svelte-ignore a11y_click_events_have_key_events -->
                    <!-- svelte-ignore a11y_no_static_element_interactions -->
                    <oc-button-v1
                        variant="secondary"
                        disabled={context.selectedVariations.length <= 0}
                        onclick={onDeleteAllClicked}
                    >
                        Alle löschen
                    </oc-button-v1>
                    <!-- svelte-ignore a11y_click_events_have_key_events -->
                    <!-- svelte-ignore a11y_no_static_element_interactions -->
                    <oc-button-v1
                        variant="primary"
                        disabled={!context.compareLink || selectedVariationCount <= 1}
                        onclick={onCompareLinkClicked}
                    >
                        <a href={context.compareLink}>Auswahl vergleichen</a>
                    </oc-button-v1>
                </div>
            </div>
        {/if}
    </div>
    {#if open}
        <div transition:fade class="reptile-comparison-sheet__backdrop" onclick={toggleOpen} aria-hidden="true"></div>
    {/if}
{/if}

<style lang="scss">
    @use '@otto-ec/design-tokens/tokens';
    @use '@otto-ec/otto-components-utils/scss/mixins';

    .reptile-comparison-sheet {
        position: fixed;
        z-index: 9000;
        right: 0;
        bottom: 0;

        width: 100vw;
        border-radius: tokens.$oc-semantic-border-radius-300 tokens.$oc-semantic-border-radius-300 0 0;

        background-color: tokens.$oc-semantic-color-canvas-background;
        filter: tokens.$oc-semantic-shadow-sticky;

        @include mixins.breakpoint-from-m($to: false, $containerFirst: false) {
            width: 448px;
            border-top-right-radius: 0;
        }

        &__tiles {
            padding: tokens.$oc-semantic-spacing-100 0;
        }

        &__hint {
            flex: 1 0 0;
        }

        &__header {
            padding: tokens.$oc-semantic-spacing-150 tokens.$oc-semantic-spacing-100 tokens.$oc-semantic-spacing-100;

            &__row {
                --oc-semantic-color-overlay-hover: transparent;
                --oc-semantic-color-overlay-active: transparent;
                --custom-spacing-y: 0;
            }
        }

        &__footer {
            display: grid;
            grid-template-columns: auto 1fr;
            gap: tokens.$oc-semantic-spacing-50;
            padding: tokens.$oc-semantic-spacing-150 tokens.$oc-semantic-spacing-100;

            @include mixins.breakpoint-from-m($to: false, $containerFirst: false) {
                grid-template-columns: 1fr 1fr;
            }
        }

        &__backdrop {
            touch-action: none;

            position: fixed;
            z-index: 8999;
            top: 0;
            left: 0;

            box-sizing: border-box;
            width: 100%;
            height: 100vh;

            background-color: tokens.$oc-semantic-color-scrim;
        }
    }

    :global {
        .reptile-comparison--no-scroll {
            overflow: hidden;
        }
    }
</style>
